import { useQuery } from '@tanstack/react-query'
import { confirmSoftwareCategories, saveSoftwareCategory, softwareCategoriesQuery } from '../../api/admSoftware'
import { Content } from '../../ui/Shell'
import { toast } from '../../ui/notify'
import { usePageStyles } from '../../ui/usePageStyles'
import { shellQuery } from '../../api/shell'
import { AdmPageHead } from './AdmPageHead'

/**
 * ADM -> Software categories, din
 * `views/version-3/softsee/adm/software/categories.php`.
 *
 * DE CE EXISTA ECRANUL: regula de semnificatie clasifica dupa NUMELE
 * programului. Un furnizor care si-a asezat deja trei sute de aplicatii pe
 * categorii proprii nu castiga nimic din asta - iar o categorie numita
 * „Aplicatii core banking" nu seamana cu niciun tipar din lista noastra, desi
 * omul stie foarte bine ce e. Aici se leaga cele doua o data.
 *
 * SUGESTIILE NU SE APLICA SINGURE. Se arata, si se confirma dintr-un clic. O
 * potrivire ghicita dupa nume care ar fi inceput sa scrie fise in CMDB fara sa
 * spuna nimeni nu e o usurare, e o surpriza.
 *
 * Se salveaza LA ALEGERE, nu in spatele unui buton: un tabel de dropdown-uri cu
 * un Save la capat e un formular pe care nimeni nu-l termina.
 */

const STYLES = ['css/adm.css']

export function SoftwareCategoriesPage() {
  const chrome = useQuery(shellQuery('softsee'))
  usePageStyles(STYLES, chrome.data?.version ?? '')

  const page = useQuery(softwareCategoriesQuery())

  const rows = page.data?.rows ?? []
  const categories = page.data?.categories ?? []
  const levels = page.data?.levels ?? []
  const canWrite = page.data?.canWrite ?? false
  const suggested = page.data?.suggested ?? 0

  const levelLabel = (value: number): string => levels.find((l) => l.value === String(value))?.label ?? ''

  const save = async (categoryId: number, category: string) => {
    try {
      const answer = await saveSoftwareCategory(categoryId, category)
      toast(answer.message, 'Software categories', answer.status === 'OK' ? 'success' : 'error')
      if (answer.status === 'OK') void page.refetch()
    } catch (error) {
      toast((error as Error).message, 'Software categories', 'error')
    }
  }

  const confirmAll = async () => {
    try {
      const answer = await confirmSoftwareCategories()
      toast(answer.message, 'Software categories', 'success')
      void page.refetch()
    } catch (error) {
      toast((error as Error).message, 'Software categories', 'error')
    }
  }

  return (
    <Content>
      <div id="content">
        <AdmPageHead
          tone="blue-hoki"
          icon="fa-tags"
          title="Your categories, and what they mean"
          note="the categories you sorted your applications into, matched against the thirteen the platform uses to decide what belongs in the CMDB"
          stats={[
            { value: rows.length.toLocaleString('en-US'), label: 'yours', tone: 'grey' },
            { value: suggested.toLocaleString('en-US'), label: 'suggested', tone: suggested > 0 ? 'yellow' : 'grey' },
          ]}
          actions={
            canWrite && suggested > 0 ? (
              <button type="button" className="btn btn-sm btn-info" id="admConfirmAll" onClick={() => void confirmAll()}>
                <i className="fas fa-check-double"></i> Confirm all {suggested} suggestions
              </button>
            ) : undefined
          }
        />

        <div className="adm-card adm-tone-blue-hoki" style={{ marginBottom: 16 }}>
          <div className="adm-card-head">
            <h5>
              <i className="fas fa-info-circle"></i> What this changes
            </h5>
            <span className="adm-card-note">only for applications the platform cannot name on its own</span>
          </div>
          <p className="adm-muted" style={{ margin: 0 }}>
            An application is placed by three things, in this order: what somebody decided about{' '}
            <strong>that application</strong>, then <strong>the category you put it in</strong> once it is mapped here,
            and only then the platform's own name patterns. Mapping a category does not overrule a decision made about a
            single application.
          </p>
        </div>

        <table className="table table-hover adm-list" id="admCategoriesTable" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>Your category</th>
              <th style={{ width: 110 }}>Applications</th>
              <th style={{ width: 280 }}>Means</th>
              <th>What that gets them</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={4} className="adm-muted" style={{ padding: 22, textAlign: 'center' }}>
                  You have not created any application categories yet. The platform falls back to its own name patterns,
                  which is what it does today.
                </td>
              </tr>
            ) : null}
            {rows.map((row) => {
              /* Cand nu s-a hotarat nimic, casuta arata SUGESTIA - dar randul
                 spune limpede ca ea nu s-a aplicat inca. */
              const hint = row.category === null && row.suggested ? row.suggested : ''
              const shown = row.category !== null ? row.category : hint
              const chosen = row.category !== null ? categories.find((c) => c.value === row.category) : undefined

              return (
                <tr key={row.id} data-id={row.id}>
                  <td>
                    <strong>{row.name}</strong>
                  </td>
                  <td className="adm-muted">{row.apps.toLocaleString('en-US')}</td>
                  <td>
                    <select
                      className="form-control input-sm admCategoryPick"
                      disabled={!canWrite}
                      value={shown}
                      onChange={(e) => void save(row.id, e.target.value)}
                    >
                      <option value="">&mdash; the platform decides by name &mdash;</option>
                      {categories.map((one) => (
                        <option key={one.value} value={one.value}>
                          {one.label}
                        </option>
                      ))}
                    </select>
                    {row.category === null && hint !== '' ? (
                      <div className="adm-muted" style={{ fontSize: 11, marginTop: 4 }}>
                        <i className="fas fa-lightbulb"></i> suggested &mdash; not applied until you save it
                      </div>
                    ) : null}
                  </td>
                  <td className="adm-muted">
                    {chosen ? (
                      <>
                        On a server: <strong>{levelLabel(chosen.server)}</strong> &middot; on a workstation:{' '}
                        <strong>{levelLabel(chosen.client)}</strong>
                        {chosen.ceiling < 2 ? (
                          <>
                            <br />
                            Never a configuration item, whatever the signals say.
                          </>
                        ) : null}
                      </>
                    ) : (
                      'Placed by name, one application at a time.'
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </Content>
  )
}
